import styled from '@emotion/styled';
import Link from 'next/link';

import { screen } from '@/components/common/breakpoints';
import { BulletPoints } from '@/components/common/bulletpoints';
import { Icon } from '@/components/common/Icon';
import { Image } from '@/components/common/image';
import { Heading, HeadingLevel, Paragraph } from '@/components/common/MarkUp';
import { PortableText } from '@/components/common/utils';
import { getBulletPointColour, getTextColour } from '@/lib/colourUtils';

import type {
  SurfaceColourTokenType,
  TextColourTokenType,
} from '@/types/colours';
import type { SanityBlock, SanityKeyed } from '@/types/sanity';
import type { SanityImageAsset, SanityReference } from 'sanity-codegen';

export interface ProgrammeCardProps {
  caption?: string;
  isCaptionUppercase?: boolean;
  heading: string;
  bgColour: SurfaceColourTokenType;
  url?: string;
  ctaCaption?: string;
  trackingTag?: {
    _type: 'trackingTag';
    blockName: string;
    blockAction: string;
  };
  featuresCaption?: string;
  features?: Array<SanityKeyed<SanityBlock>>;
  body: string;
  icon: {
    _type: 'image';
    asset: SanityReference<SanityImageAsset>;
  };
}

const ConditionalLinkWrapper = ({ url, trackingTag, children }) =>
  url ? (
    <LinkCardWrapper
      href={url}
      className={
        trackingTag?.blockName &&
        `tracking-${trackingTag.blockName}__${trackingTag.blockAction}`
      }
    >
      {children}
    </LinkCardWrapper>
  ) : (
    children
  );

export const ProgrammeCard = ({
  caption,
  isCaptionUppercase = true,
  heading,
  bgColour,
  url,
  ctaCaption = 'Learn more',
  featuresCaption = 'What you get',
  features,
  body,
  icon,
  trackingTag,
}: ProgrammeCardProps) => {
  return (
    <ConditionalLinkWrapper trackingTag={trackingTag} url={url}>
      <Container bgColour={bgColour} url={url}>
        {icon && (
          <StyledIcon>
            <Image {...icon} sizes="18px" />
          </StyledIcon>
        )}
        {caption && (
          <Caption
            isCaptionUppercase={isCaptionUppercase}
            textColour={getTextColour(bgColour, {
              action: false,
              isCaption: true,
            })}
            size="caption-lg"
          >
            {caption}
          </Caption>
        )}
        <HeadingLevel>
          <Title size="body-md-semi-bold">{heading}</Title>
        </HeadingLevel>
        <Body textColour={getTextColour(bgColour)} size="body-md">
          {body}
        </Body>
        <Content>
          {features && (
            <FeatureList>
              <Caption size="caption-lg" textColour={getTextColour(bgColour)}>
                {featuresCaption}
              </Caption>
              <BulletPoints pointColour={getBulletPointColour(bgColour)}>
                <PortableText value={features} />
              </BulletPoints>
            </FeatureList>
          )}
        </Content>
        {url && (
          <CTA>
            {ctaCaption} <StyledChevron icon="ChevronRight" size="12px" />
          </CTA>
        )}
      </Container>
    </ConditionalLinkWrapper>
  );
};

const StyledChevron = styled(Icon)`
  margin-top: 2px;
  margin-left: 8px;
`;

const CTA = styled.span`
  font-size: var(--font-size-xx-small);
  display: flex;
  justify-content: center;
  margin-top: var(--spacing-x-small);
  font-weight: var(--font-weight-bold);
  padding: var(--spacing-small) var(--spacing-small);
  border-top: 1px solid var(--colour-border-subtle);
`;

const Content = styled.div`
  flex-grow: 1;
`;

const Container = styled.div<{ bgColour: SurfaceColourTokenType; url: string }>`
  background-color: ${({ bgColour }) => `var(${bgColour})`};
  padding: var(--spacing-400) var(--spacing-250)
    ${({ url }) => (url ? `var(--spacing-xxx-small)` : `var(--spacing-250)`)}
    var(--spacing-250);
  border-radius: 8px;
  max-width: 308px;
  width: 100%;
  height: 100%;
  position: relative;
  transition: transform 0.3s;
  display: flex;
  flex-direction: column;

  & > a {
    text-decoration: none;
  }

  &:hover {
    cursor: ${({ url }) => (url ? 'pointer' : 'default')};
  }

  &:hover > ${CTA} {
    text-decoration: underline;
  }

  ${screen.md} {
    &:hover {
      transform: scale(1.03);
    }
  }
`;

const Caption = styled(Paragraph)<{
  textColour: TextColourTokenType;
  isCaptionUppercase?: boolean;
}>`
  margin-bottom: var(--spacing-xxx-small);
  color: ${({ textColour }) => `var(${textColour})`};
  font-weight: var(--font-weight-bold);
  text-transform: ${({ isCaptionUppercase }) =>
    isCaptionUppercase ? 'uppercase' : 'none'};
`;

const Title = styled(Heading)`
  margin-bottom: var(--spacing-xxx-small);
  color: var(--text-strong);
  font-weight: var(--font-weight-bold);
`;

const Body = styled(Paragraph)<{ textColour: TextColourTokenType }>`
  margin-bottom: var(--spacing-xxx-small);
  color: ${({ textColour }) => `var(${textColour})`};
`;

const FeatureList = styled.div`
  margin-top: var(--spacing-x-small);
`;

const LinkCardWrapper = styled(Link)`
  text-decoration: none;
  color: inherit;
  &hover {
    text-decoration: none;
  }
`;

const StyledIcon = styled.div`
  height: 18px;
  width: 18px;

  position: absolute;
  right: 15.5px;
  top: 15.5px;

  ${screen.lg} {
    width: 21px;
    height: 21px;
  }
`;
